import React from "react";
import { Select, Space, Divider } from "antd";
import { TRANSACTION_TYPES, TRANSACTION_TYPE_LABELS, TRANSACTION_STATUS, TRANSACTION_TYPE_SPECIFIC_STATUS, TRANSACTION_FILTER_OPTIONS, TRANSACTION_FILTER_STATUS_LABELS, TRANSACTION_FILTER_STATUS_COLORS, TRANSACTION_FILTER_STATUS_ICONS, TRANSACTION_MIGRATION_STATUS } from "../constants";

const { Option } = Select;

/**
 * 事务筛选选择器组件
 * 用于选择特定事务类型的状态筛选条件
 *
 * @param {Object} props
 * @param {string} props.transactionType - 事务类型
 * @param {Array} props.value - 当前选中的状态值数组
 * @param {function} props.onChange - 状态变化回调
 * @param {string} props.placeholder - 占位符文本
 * @param {boolean} props.allowClear - 是否允许清空
 */
const TransactionFilterSelector = ({ transactionType, value = [], onChange, placeholder = "选择状态", allowClear = true }) => {
    const getRelevantStatuses = () => {
        // 优先使用特定事务类型的状态定义
        if (TRANSACTION_TYPE_SPECIFIC_STATUS[transactionType]) {
            return Object.values(TRANSACTION_TYPE_SPECIFIC_STATUS[transactionType]);
        }
        // 否则，回退到通用事务状态
        return Object.values(TRANSACTION_STATUS);
    };

    const relevantStatuses = getRelevantStatuses();

    // 构建选项列表 (只包含 Select.Option 使用的数据，不含JSX)
    const buildSelectOptions = () => {
        const options = [];
        // 特殊选项
        options.push({
            key: TRANSACTION_FILTER_OPTIONS.UNSET,
            value: TRANSACTION_FILTER_OPTIONS.UNSET,
            label: TRANSACTION_FILTER_STATUS_LABELS[TRANSACTION_FILTER_OPTIONS.UNSET],
            color: TRANSACTION_FILTER_STATUS_COLORS[TRANSACTION_FILTER_OPTIONS.UNSET],
            icon: TRANSACTION_FILTER_STATUS_ICONS[TRANSACTION_FILTER_OPTIONS.UNSET],
        });
        // 标准状态选项 (基于 relevantStatuses)
        relevantStatuses.forEach((status) => {
            options.push({
                key: status, // key for React list
                value: status, // value for Select
                label: TRANSACTION_FILTER_STATUS_LABELS[status] || status,
                color: TRANSACTION_FILTER_STATUS_COLORS[status] || TRANSACTION_FILTER_STATUS_COLORS.default,
                icon: TRANSACTION_FILTER_STATUS_ICONS[status] || TRANSACTION_FILTER_STATUS_ICONS.default,
            });
        });
        // 迁移状态选项
        Object.values(TRANSACTION_MIGRATION_STATUS).forEach((migrationStatus) => {
            options.push({
                key: migrationStatus,
                value: migrationStatus,
                label: TRANSACTION_FILTER_STATUS_LABELS[migrationStatus] || migrationStatus,
                color: TRANSACTION_FILTER_STATUS_COLORS[migrationStatus] || TRANSACTION_FILTER_STATUS_COLORS.default,
                icon: TRANSACTION_FILTER_STATUS_ICONS[migrationStatus] || TRANSACTION_FILTER_STATUS_ICONS.default,
            });
        });
        return options;
    };

    const selectOptions = buildSelectOptions(); // 用于filterOption的数据源

    return (
        <Select
            mode="multiple"
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            allowClear={allowClear}
            style={{ width: "100%" }}
            maxTagCount={2}
            optionFilterProp="label" // 使用 Option 的 label 属性进行筛选
            filterOption={(input, option) => {
                // option.label 是 Ant Design Select Option 内部传递的
                // 它会是 <Space> 包裹的 JSX 元素的文本内容
                // 所以我们需要直接用 option.children 或者 option.label 来比较
                const label = option.label || option.children;
                if (typeof label === "string") {
                    return label.toLowerCase().includes(input.toLowerCase());
                } else if (Array.isArray(label)) {
                    // children might be an array of React elements
                    // Try to extract text content from children for filtering
                    const textContent = label.map((child) => (typeof child === "string" ? child : child && child.props && child.props.children ? (Array.isArray(child.props.children) ? child.props.children.join("") : child.props.children) : "")).join("");
                    return textContent.toLowerCase().includes(input.toLowerCase());
                }
                return false;
            }}
        >
            {/* 特殊选项组 */}
            <Option key={TRANSACTION_FILTER_OPTIONS.UNSET} value={TRANSACTION_FILTER_OPTIONS.UNSET} label={TRANSACTION_FILTER_STATUS_LABELS[TRANSACTION_FILTER_OPTIONS.UNSET]}>
                <Space size="small">
                    <span style={{ color: TRANSACTION_FILTER_STATUS_COLORS[TRANSACTION_FILTER_OPTIONS.UNSET] }}>{TRANSACTION_FILTER_STATUS_ICONS[TRANSACTION_FILTER_OPTIONS.UNSET]}</span>
                    <span style={{ color: TRANSACTION_FILTER_STATUS_COLORS[TRANSACTION_FILTER_OPTIONS.UNSET] }}>{TRANSACTION_FILTER_STATUS_LABELS[TRANSACTION_FILTER_OPTIONS.UNSET]}</span>
                </Space>
            </Option>

            {/* 标准状态选项 - 基于 relevantStatuses */}
            {relevantStatuses.map((status) => (
                <Option key={status} value={status} label={TRANSACTION_FILTER_STATUS_LABELS[status] || status}>
                    <Space size="small">
                        <span style={{ color: TRANSACTION_FILTER_STATUS_COLORS[status] || TRANSACTION_FILTER_STATUS_COLORS.default }}>{TRANSACTION_FILTER_STATUS_ICONS[status] || TRANSACTION_FILTER_STATUS_ICONS.default}</span>
                        <span style={{ color: TRANSACTION_FILTER_STATUS_COLORS[status] || TRANSACTION_FILTER_STATUS_COLORS.default }}>{TRANSACTION_FILTER_STATUS_LABELS[status] || status}</span>
                    </Space>
                </Option>
            ))}
        </Select>
    );
};

export default TransactionFilterSelector;
